import { Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
// import { SidebarCollapseService } from '@services/sidebar-collapse.service';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '@app/services/auth/auth.service';
import { UserAuthorizationService } from '@app/services/user-authorization.service';
import { LoadingToasterComponent } from '@shared/components/loading-toaster/loading-toaster.component';
import { OpenReplayService } from '@app/services/open-replay/open-replay.service';
import { environment } from '@env/environment';
import { ENVIRONMENT_NAMES } from '@shared/constants/constants';
import { AppVersionService } from '@services/app-version.service';
import { CacheService } from '@services/cache.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ToastModule,
    LoadingToasterComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly userAuthorizationService = inject(UserAuthorizationService);
  private readonly openReplayService = inject(OpenReplayService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly cacheService = inject(CacheService);
  private readonly destroyRef = inject(DestroyRef);

  private appVersionTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.appVersionTimeout !== null) {
        clearTimeout(this.appVersionTimeout);
      }
    });
  }

  ngOnInit(): void {
    if (
      environment.environmentName !== ENVIRONMENT_NAMES.LOCAL &&
      environment.environmentName !== ENVIRONMENT_NAMES.DEVELOPMENT
    ) {
      this.startOpenReplay();
    }

    this.appVersionInitiator();

    // Initialize authentication
    this.authService
      .initAuth()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: isAuthenticated => {
          if (isAuthenticated) {
            // Get user data from the signal (which gets populated from JWT tokens)
            const userData = this.userAuthorizationService.getCurrentUser();

            // If no user data found, try to restore from stored tokens
            if (!userData) {
              this.authService.restoreUserDataFromStorage();
            }

            // Set user data in OpenReplay after authentication
            if (userData?.id) {
              this.openReplayService.restartTracking(userData?.id);
            }

            // Load tenant config after auth !!!
            this.authService.loadTenantConfiguration();
          }
        },
        error: error => {
          console.error('Authentication initialization failed:', error);
        },
      });
  }

  startOpenReplay() {
    if (this.openReplayService.checkTrackerIsActive()) return;
    this.openReplayService.start().catch(err => {
      this.openReplayService.tracker = null;
      console.error('Failed to start OpenReplay:', err);
      // Try to start again in 10 seconds
      setTimeout(() => this.startOpenReplay(), 10000);
    });
  }

  private appVersionInitiator(): void {
    if (this.appVersionTimeout !== null) clearTimeout(this.appVersionTimeout);
    this.appVersionTimeout = setTimeout(
      () => this.appVersionCheck(),
      5 * 60 * 1000,
    ); // 5 minutes
  }

  private appVersionCheck(): void {
    this.appVersionService
      .checkVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          if (
            data?.version &&
            this.appVersionService.shouldReload(data.version)
          ) {
            this.appVersionService.setVersionReloadIntent(true);
            // If new version, clear cache and reload the page
            // We clear the cache first because reload hits the cache
            this.cacheService.clearCacheForApp().then(() => {
              window.location.reload();
            });
          }
          this.appVersionInitiator();
        },
        error: () => {
          this.appVersionService.setVersionReloadIntent(false);
          this.appVersionInitiator();
        },
      });
  }
}
